/**
 * @fileoverview React Context provider for bootstrap state management.
 *
 * This module provides a centralized state management solution for the two-phase
 * bootstrap process.
 *
 * The BootstrapProvider:
 * - Manages loading, completion, and error states via useReducer
 * - Maintains a single shared AbortController for cancellation
 * - Handles React Strict Mode correctly (double mount/unmount)
 * - Provides actions for abort, retry, and state reset
 *
 * ## React Strict Mode Considerations
 *
 * In React 18+ Strict Mode, components are mounted, unmounted, and remounted
 * during development. This provider handles this by:
 *
 * 1. Using a single AbortController stored in useRef - the ref persists across
 *    the initial unmount in Strict Mode, but the cleanup effect properly aborts
 *    the controller on real unmounts.
 *
 * 2. Tracking mounted state via isMountedRef to prevent state updates after
 *    unmount.
 *
 * 3. The reducer maintains completion flags that survive Strict Mode cycles
 *    since they are part of React state (not module-level variables).
 *
 * ## Architecture
 *
 * ```
 * BootstrapProvider
 *   └─ useReducer(bootstrapReducer, initialState)
 *   └─ useRef(AbortController)  <- Shared across both phases
 *   └─ BootstrapContext.Provider
 *        ├─ state: BootstrapContextState
 *        ├─ dispatch: React.Dispatch<BootstrapAction>
 *        ├─ abortController: AbortController | null
 *        ├─ abortBootstrap: () => void
 *        └─ retryBootstrap: () => void
 * ```
 *
 * @see react-ui/src/hooks/bootstrap/types.ts for type definitions
 * @see docs/adr/0002-bootstrap-state-hydration.md for architecture decisions
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useRef,
  useCallback,
  useEffect,
  type ReactNode,
  type Dispatch,
} from 'react';
import {
  type BootstrapContextState,
  type BootstrapAction,
  createInitialBootstrapState,
} from './types';
import { logger } from '../../services/logger.client';

// =============================================================================
// Context Value Type
// =============================================================================

/**
 * Value provided by the BootstrapContext.
 *
 * Contains the current state, dispatch function for reducer actions,
 * the shared AbortController, and convenience action methods.
 */
export interface BootstrapContextValue {
  /**
   * Current bootstrap state.
   * Contains loading flags, completion flags, errors, and current phase.
   */
  state: BootstrapContextState;

  /**
   * Dispatch function for sending actions to the reducer.
   * Use this for fine-grained state updates during bootstrap operations.
   */
  dispatch: Dispatch<BootstrapAction>;

  /**
   * The shared AbortController for bootstrap operations.
   * Both critical and cache phases use this single controller.
   * Will be null if bootstrap hasn't started or was aborted.
   */
  abortController: AbortController | null;

  /**
   * Aborts the current bootstrap operation and resets state.
   *
   * This method:
   * 1. Aborts the shared AbortController (cancels in-flight requests)
   * 2. Creates a new AbortController for future operations
   * 3. Dispatches RESET action to clear all state
   *
   * Use this when:
   * - User logs out during bootstrap
   * - User navigates away and bootstrap should stop
   * - An unrecoverable error requires starting fresh
   */
  abortBootstrap: () => void;

  /**
   * Prepares state for a retry attempt after an error.
   *
   * This method:
   * 1. Aborts the current AbortController (if any)
   * 2. Creates a fresh AbortController
   * 3. Dispatches RESET action to clear error state
   *
   * Does NOT automatically start the bootstrap - the consumer is
   * responsible for triggering the bootstrap fetch after calling this.
   *
   * Use this when:
   * - User clicks a "Retry" button after a bootstrap error
   * - The application determines it should automatically retry
   */
  retryBootstrap: () => void;

  /**
   * Creates a new AbortController for bootstrap operations.
   *
   * Use this when starting a new bootstrap fetch. This method:
   * 1. Aborts any existing AbortController
   * 2. Creates and stores a new AbortController
   * 3. Returns the new controller for use in fetch operations
   *
   * @returns The newly created AbortController
   */
  createAbortController: () => AbortController;
}

// =============================================================================
// Reducer
// =============================================================================

/**
 * Reducer function for bootstrap state transitions.
 *
 * Handles all BootstrapAction types and maintains computed properties
 * (isAnyLoading, isFullyBootstrapped, hasFatalError) automatically.
 *
 * State Machine:
 * ```
 *   idle ──START_CRITICAL──> critical ──CRITICAL_SUCCESS──> cache ──CACHE_SUCCESS──> complete
 *     │                          │                            │
 *     │                    CRITICAL_ERROR                CACHE_ERROR
 *     │                          │                            │
 *     │                          v                            v
 *     │                        error                       complete (cache errors are non-fatal)
 *     │
 *     └──────────────────────────────────────────────────── RESET (from any state)
 * ```
 *
 * @param state - Current bootstrap state
 * @param action - The action to process
 * @returns The new bootstrap state
 */
// eslint-disable-next-line react-refresh/only-export-components
export function bootstrapReducer(
  state: BootstrapContextState,
  action: BootstrapAction
): BootstrapContextState {
  logger.debug(
    { currentPhase: state.phase, actionType: action.type },
    'BootstrapProvider: Reducer processing action'
  );

  switch (action.type) {
    case 'START_CRITICAL': {
      // Only start if we're in idle or resetting after an error
      // Ignore if already loading or completed
      if (state.isCriticalLoading || state.hasCriticalCompleted) {
        logger.warn(
          {
            isCriticalLoading: state.isCriticalLoading,
            hasCriticalCompleted: state.hasCriticalCompleted,
          },
          'BootstrapProvider: Ignoring START_CRITICAL - already loading or completed'
        );
        return state;
      }

      return {
        ...state,
        phase: 'critical',
        isCriticalLoading: true,
        isCacheLoading: false,
        isAnyLoading: true,
        hasFatalError: false,
        criticalError: null,
      };
    }

    case 'CRITICAL_SUCCESS': {
      // Transition to cache phase - critical is done, cache will start
      logger.info(
        { payloadKeys: Object.keys(action.payload) },
        'BootstrapProvider: Critical bootstrap succeeded'
      );

      return {
        ...state,
        phase: 'cache',
        isCriticalLoading: false,
        hasCriticalCompleted: true,
        criticalError: null,
        hasFatalError: false,
        // isAnyLoading stays true until cache completes
        // isCacheLoading will be set true by START_CACHE
      };
    }

    case 'CRITICAL_ERROR': {
      // Fatal error - bootstrap cannot continue
      logger.error(
        {
          errorName: action.payload.name,
          errorMessage: action.payload.message,
        },
        'BootstrapProvider: Critical bootstrap failed with error'
      );

      return {
        ...state,
        phase: 'error',
        isCriticalLoading: false,
        isAnyLoading: false,
        hasFatalError: true,
        criticalError: action.payload,
      };
    }

    case 'START_CACHE': {
      // Start cache loading - only valid after critical completes
      if (!state.hasCriticalCompleted) {
        logger.warn(
          { hasCriticalCompleted: state.hasCriticalCompleted },
          'BootstrapProvider: Ignoring START_CACHE - critical not completed'
        );
        return state;
      }

      if (state.hasCacheCompleted) {
        logger.warn(
          { hasCacheCompleted: state.hasCacheCompleted },
          'BootstrapProvider: Ignoring START_CACHE - cache already completed'
        );
        return state;
      }

      return {
        ...state,
        phase: 'cache',
        isCacheLoading: true,
        isAnyLoading: true,
      };
    }

    case 'CACHE_SUCCESS': {
      // Cache loaded successfully - bootstrap fully complete
      logger.info(
        { payloadKeys: Object.keys(action.payload) },
        'BootstrapProvider: Cache bootstrap succeeded'
      );

      return {
        ...state,
        phase: 'complete',
        isCacheLoading: false,
        isAnyLoading: false,
        hasCacheCompleted: true,
        isFullyBootstrapped: true,
        cacheError: null,
      };
    }

    case 'CACHE_ERROR': {
      // Cache failed - non-fatal, we still complete bootstrap
      // The app can function without cache data (items, spells, etc.)
      logger.warn(
        {
          errorName: action.payload.name,
          errorMessage: action.payload.message,
        },
        'BootstrapProvider: Cache bootstrap failed (non-fatal)'
      );

      return {
        ...state,
        phase: 'complete',
        isCacheLoading: false,
        isAnyLoading: false,
        hasCacheCompleted: false, // Not completed, but we move on
        isFullyBootstrapped: false,
        cacheError: action.payload,
      };
    }

    case 'RESET': {
      // Full reset - return to initial state
      logger.info('BootstrapProvider: Resetting to initial state');
      return createInitialBootstrapState();
    }

    case 'ABORT': {
      // Handle abort of a specific phase
      logger.info(
        { abortedPhase: action.payload },
        'BootstrapProvider: Bootstrap phase aborted'
      );

      if (action.payload === 'critical') {
        // Critical abort - go back to idle (can retry)
        return {
          ...state,
          phase: 'idle',
          isCriticalLoading: false,
          isAnyLoading: state.isCacheLoading, // Keep cache loading state if active
        };
      } else {
        // Cache abort - mark as complete since critical is done
        return {
          ...state,
          phase: state.hasCriticalCompleted ? 'complete' : 'idle',
          isCacheLoading: false,
          isAnyLoading: state.isCriticalLoading, // Keep critical loading state if active
        };
      }
    }

    default: {
      // TypeScript exhaustiveness check
      const exhaustiveCheck: never = action;
      logger.error(
        { action: exhaustiveCheck },
        'BootstrapProvider: Unknown action type'
      );
      return state;
    }
  }
}

// =============================================================================
// Context
// =============================================================================

/**
 * React Context for bootstrap state.
 *
 * The context is initialized with undefined to enable the null check in
 * useBootstrapContext. Components must be wrapped in BootstrapProvider
 * to access bootstrap state.
 */
const BootstrapContext = createContext<BootstrapContextValue | undefined>(
  undefined
);

// Set display name for React DevTools
BootstrapContext.displayName = 'BootstrapContext';

// =============================================================================
// Provider Component
// =============================================================================

/**
 * Props for the BootstrapProvider component.
 */
export interface BootstrapProviderProps {
  /** Child components that will have access to bootstrap context */
  children: ReactNode;

  /**
   * Optional initial state for testing purposes.
   * If provided, overrides the default initial state.
   */
  initialState?: Partial<BootstrapContextState>;
}

/**
 * Provider component that wraps the application to provide bootstrap state.
 *
 * This component:
 * - Initializes the bootstrap reducer with initial state
 * - Manages a shared AbortController for cancellation
 * - Provides abort and retry action methods
 * - Handles cleanup on unmount (aborts pending requests)
 *
 * ## Usage
 *
 * Wrap your application (or the authenticated portion) with BootstrapProvider:
 *
 * ```tsx
 * function App() {
 *   return (
 *     <BootstrapProvider>
 *       <AuthenticatedApp />
 *     </BootstrapProvider>
 *   );
 * }
 * ```
 *
 * ## React Strict Mode
 *
 * In development with Strict Mode, components mount, unmount, and remount.
 * This provider handles this correctly:
 *
 * 1. The AbortController ref is created on first mount
 * 2. On unmount (including Strict Mode unmount), cleanup aborts the controller
 * 3. On remount, a new controller is created if needed
 *
 * The reducer state survives Strict Mode cycles because useReducer state
 * is preserved by React across the simulated unmount/remount.
 *
 * @param props - Component props
 * @returns The provider component wrapping children
 */
export function BootstrapProvider({
  children,
  initialState,
}: BootstrapProviderProps): React.JSX.Element {
  // Initialize reducer with merged initial state
  const mergedInitialState: BootstrapContextState = {
    ...createInitialBootstrapState(),
    ...initialState,
  };

  const [state, dispatch] = useReducer(bootstrapReducer, mergedInitialState);

  // Single shared AbortController stored in ref
  // This persists across renders and is shared by both bootstrap phases
  const abortControllerRef = useRef<AbortController | null>(null);

  // Track whether the provider is mounted to prevent state updates after unmount
  // This is especially important for React Strict Mode
  const isMountedRef = useRef<boolean>(true);

  /**
   * Creates a new AbortController, aborting any existing one first.
   *
   * This method is used when starting a new bootstrap operation.
   * It ensures only one controller is active at a time.
   */
  const createAbortController = useCallback((): AbortController => {
    // Abort any existing controller first
    if (abortControllerRef.current) {
      logger.debug(
        'BootstrapProvider: Aborting existing controller before creating new one'
      );
      abortControllerRef.current.abort();
    }

    // Create and store new controller
    const newController = new AbortController();
    abortControllerRef.current = newController;

    logger.debug('BootstrapProvider: Created new AbortController');
    return newController;
  }, []);

  /**
   * Aborts the current bootstrap operation and resets state.
   */
  const abortBootstrap = useCallback((): void => {
    logger.info('BootstrapProvider: abortBootstrap called');

    // Abort current controller
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Reset state if still mounted
    if (isMountedRef.current) {
      dispatch({ type: 'RESET' });
    }
  }, []);

  /**
   * Prepares state for a retry attempt after an error.
   */
  const retryBootstrap = useCallback((): void => {
    logger.info('BootstrapProvider: retryBootstrap called');

    // Abort current controller if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create fresh controller for retry
    abortControllerRef.current = new AbortController();

    // Reset state if still mounted
    if (isMountedRef.current) {
      dispatch({ type: 'RESET' });
    }
  }, []);

  // Cleanup effect: abort controller and mark unmounted
  // This runs on component unmount, including React Strict Mode unmounts
  useEffect(() => {
    // Mark as mounted on effect start
    isMountedRef.current = true;

    logger.debug('BootstrapProvider: Provider mounted');

    return () => {
      logger.debug('BootstrapProvider: Provider unmounting - cleaning up');

      // Mark as unmounted to prevent state updates
      isMountedRef.current = false;

      // Abort any in-progress requests
      if (abortControllerRef.current) {
        logger.info(
          'BootstrapProvider: Aborting in-progress bootstrap on unmount'
        );
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // Build context value
  // Memoization is intentionally not used here because the state changes
  // frequently during bootstrap and we want consumers to always get fresh values
  const contextValue: BootstrapContextValue = {
    state,
    dispatch,
    abortController: abortControllerRef.current,
    abortBootstrap,
    retryBootstrap,
    createAbortController,
  };

  return (
    <BootstrapContext.Provider value={contextValue}>
      {children}
    </BootstrapContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to access the bootstrap context.
 *
 * Provides access to bootstrap state, dispatch function, and action methods.
 * Must be used within a BootstrapProvider.
 *
 * @returns The bootstrap context value
 * @throws Error if used outside of BootstrapProvider
 *
 * @example
 * ```tsx
 * function BootstrapHandler() {
 *   const {
 *     state,
 *     dispatch,
 *     abortController,
 *     abortBootstrap,
 *     retryBootstrap,
 *     createAbortController
 *   } = useBootstrapContext();
 *
 *   if (state.hasFatalError) {
 *     return (
 *       <ErrorScreen
 *         error={state.criticalError}
 *         onRetry={retryBootstrap}
 *       />
 *     );
 *   }
 *
 *   if (state.isCriticalLoading) {
 *     return <LoadingScreen />;
 *   }
 *
 *   return <MainApp />;
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useBootstrapContext(): BootstrapContextValue {
  const context = useContext(BootstrapContext);

  if (context === undefined) {
    throw new Error(
      'useBootstrapContext must be used within a BootstrapProvider. ' +
        'Wrap your component tree with <BootstrapProvider> to fix this error.'
    );
  }

  return context;
}

// =============================================================================
// Convenience Hooks (Optional - for more granular access)
// =============================================================================

/**
 * Hook to access only the bootstrap state.
 *
 * Use this when you only need to read state and don't need dispatch or actions.
 * This is useful for display-only components.
 *
 * @returns The current bootstrap state
 * @throws Error if used outside of BootstrapProvider
 *
 * @example
 * ```tsx
 * function LoadingIndicator() {
 *   const state = useBootstrapState();
 *   return state.isAnyLoading ? <Spinner /> : null;
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useBootstrapState(): BootstrapContextState {
  const { state } = useBootstrapContext();
  return state;
}

/**
 * Hook to access the bootstrap dispatch function.
 *
 * Use this when you need to dispatch actions but don't need the full context.
 *
 * @returns The dispatch function for bootstrap actions
 * @throws Error if used outside of BootstrapProvider
 *
 * @example
 * ```tsx
 * function BootstrapTrigger() {
 *   const dispatch = useBootstrapDispatch();
 *
 *   const handleStart = () => {
 *     dispatch({ type: 'START_CRITICAL' });
 *   };
 *
 *   return <button onClick={handleStart}>Start Bootstrap</button>;
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useBootstrapDispatch(): Dispatch<BootstrapAction> {
  const { dispatch } = useBootstrapContext();
  return dispatch;
}

/**
 * Hook to access bootstrap action methods.
 *
 * Provides convenient methods for common operations without needing
 * the full context. Useful for error handling and retry logic.
 *
 * @returns Object with abort and retry methods
 * @throws Error if used outside of BootstrapProvider
 *
 * @example
 * ```tsx
 * function ErrorHandler({ error }: { error: Error }) {
 *   const { retryBootstrap } = useBootstrapActions();
 *
 *   return (
 *     <div>
 *       <p>Error: {error.message}</p>
 *       <button onClick={retryBootstrap}>Retry</button>
 *     </div>
 *   );
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useBootstrapActions(): Pick<
  BootstrapContextValue,
  'abortBootstrap' | 'retryBootstrap' | 'createAbortController'
> {
  const { abortBootstrap, retryBootstrap, createAbortController } =
    useBootstrapContext();
  return { abortBootstrap, retryBootstrap, createAbortController };
}
